import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface InvoiceUploaderProps {
  onUploadComplete?: () => void;
}

export default function InvoiceUploader({ onUploadComplete }: InvoiceUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setResult(null);

    try {
      const file = acceptedFiles[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/invoices/upload', {
        method: 'POST',
        body: formData,
      });

      const data = (await response.json()) as { findings?: number; error?: string };

      if (response.ok) {
        setResult({
          success: true,
          message: `Invoice uploaded successfully. ${data.findings} findings detected.`,
        });
        onUploadComplete?.();
      } else {
        setResult({
          success: false,
          message: data.error || 'Upload failed',
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'Upload failed. Please try again.',
      });
    } finally {
      setUploading(false);
    }
  }, [onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    maxFiles: 1,
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all
          ${isDragActive
            ? 'border-cyan-500 bg-cyan-500/10'
            : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
          }
        `}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center gap-4">
          {uploading ? (
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
          ) : (
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
              {isDragActive ? (
                <Upload className="w-8 h-8 text-cyan-400" />
              ) : (
                <FileText className="w-8 h-8 text-cyan-400" />
              )}
            </div>
          )}
          
          <div>
            <p className="text-lg font-medium text-white mb-1">
              {uploading ? 'Uploading...' : isDragActive ? 'Drop file here' : 'Upload invoice'}
            </p>
            <p className="text-sm text-slate-400">
              Drag & drop or click to select PDF or image
            </p>
          </div>
        </div>
      </div>

      {result && (
        <div
          className={`
            rounded-lg p-4 flex items-start gap-3
            ${result.success
              ? 'bg-emerald-500/10 border border-emerald-500/20'
              : 'bg-red-500/10 border border-red-500/20'
            }
          `}
        >
          {result.success ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          )}
          <p className={`text-sm ${result.success ? 'text-emerald-400' : 'text-red-400'}`}>
            {result.message}
          </p>
        </div>
      )}
    </div>
  );
}
